import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SEMANTIC_COLOR_VARS, hashSemanticColor } from '../../../shared/components/semanticColor';
import { formatEntityTypeLabel } from '../../../shared/components/entityTypeLabel';
import { formatRelativeTime } from '../../../shared/components/relativeTime';
import type { EntityTypeEnrichment } from '../../../core/types/data-models';

/**
 * Newest insight topics across every managed type — the "what did the AI just
 * work on" strip above the type cards. Each chip opens its topic's tab.
 */
export function RecentAnalysesStrip({
  topics,
  onOpen,
}: {
  topics: EntityTypeEnrichment[];
  onOpen: (topic: EntityTypeEnrichment) => void;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
        <AutoAwesomeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
        <Typography variant="overline" color="text.secondary" lineHeight={1.6}>
          Recent Analyses
        </Typography>
      </Stack>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {topics.map((topic) => {
          const topicVars = SEMANTIC_COLOR_VARS[hashSemanticColor(topic.entityType)];
          const topicTypeLabel = formatEntityTypeLabel(topic.entityType);
          return (
            <Tooltip key={topic.id} title={`Open ${topic.topicTitle} · ${topicTypeLabel}`}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                onClick={() => onOpen(topic)}
                sx={{
                  px: 1,
                  py: 0.5,
                  minWidth: 0,
                  maxWidth: 260,
                  borderRadius: 'var(--radius-md)',
                  bgcolor: topicVars.soft,
                  border: '1px solid',
                  borderColor: topicVars.line,
                  cursor: 'pointer',
                  transition: 'filter var(--transition)',
                  // The tint is a CSS variable, so a brightness shift
                  // gives the hover cue in either theme.
                  '&:hover': {
                    filter: (theme) =>
                      theme.palette.mode === 'dark' ? 'brightness(1.15)' : 'brightness(0.96)',
                  },
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 13, color: topicVars.main, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    noWrap
                    display="block"
                    lineHeight={1.35}
                  >
                    {topic.topicTitle}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    display="block"
                    lineHeight={1.35}
                    sx={{ fontSize: 10.5 }}
                  >
                    {topicTypeLabel} · {formatRelativeTime(topic.updatedAt)}
                  </Typography>
                </Box>
              </Stack>
            </Tooltip>
          );
        })}
      </Box>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}
